import type { DateToString } from '@/types/util/dateToString'

/**
 * UNIXタイムスタンプを日付にデシリアイズする関数
 * @param unixTimestamp
 * @returns {Date} デシリアイズされた日付オブジェクト
 */
export function deserializeDateFromUnix(unixTimestamp: number) {
  return new Date(unixTimestamp * 1000)
}

/**
 * 日付をフォーマット
 * @param date
 * @param formatStr
 * @returns {string} フォーマットされた日付文字列
 */
export function formatDate(date?: Date | string, formatStr = 'YYYY/MM/DD') {
  if (!date) return ''

  if (typeof date === 'string') {
    date = new Date(date)
  }

  const options: Intl.DateTimeFormatOptions = {}

  if (formatStr.includes('YYYY')) {
    options.year = 'numeric'
  }
  if (formatStr.includes('MM')) {
    options.month = '2-digit'
  }
  if (formatStr.includes('DD')) {
    options.day = '2-digit'
  }

  // デフォルト区切りは '/'
  let delimiter = '/'
  // formatStrから区切り文字を抽出
  const delimMatch = formatStr.match(/[^YMD]+/)
  if (delimMatch) {
    delimiter = delimMatch[0]
  }

  // 日付を現地表記で生成
  const output = date.toLocaleDateString('ja-JA', options)
  // 区切り記号を formatStr のものに置換して返却
  return output.replace(/\//g, delimiter)
}

export function jaFormatDate(date?: Date | string, formatStr = 'YYYY年MM月DD日') {
  if (!date) return ''

  if (typeof date === 'string') {
    date = new Date(date)
  }

  const year = date.getFullYear()
  const month = ('0' + (date.getMonth() + 1)).slice(-2)
  const day = ('0' + date.getDate()).slice(-2)

  return formatStr.replace(/YYYY/g, year.toString()).replace(/MM/g, month).replace(/DD/g, day)
}

export const toISODateString = (date: Date | string) => {
  if (typeof date === 'string') {
    date = new Date(date)
  }
  return date.toISOString().split('T')[0]
}

export const toISODatetimeString = (date: Date | string) => {
  if (typeof date === 'string') {
    date = new Date(date)
  }
  return date.toISOString()
}

export const formatDatetime = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short'
  }

  const dateString = date.toLocaleDateString('ja-JP', options)

  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${dateString} ${hours}:${minutes}`
}

type JsonValue = string | number | boolean | null | JsonObject | Array<JsonValue> | Date
interface JsonObject {
  [key: string]: JsonValue
}

export function convertIsoStringsToDate<T>(value: DateToString<T>): T | Date {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?[Zz]?/

  if (Array.isArray(value)) {
    return value.map(item => convertIsoStringsToDate(item)) as T
  } else if (typeof value === 'object' && value !== null) {
    const newObj: JsonObject = {}
    for (const [k, v] of Object.entries(value)) {
      const _value = v as JsonValue
      newObj[k] =
        typeof _value === 'string' && isoDateRegex.test(_value)
          ? new Date(_value)
          : convertIsoStringsToDate<DateToString<JsonValue>>(_value)
    }
    return newObj as T
  }

  return typeof value === 'string' && isoDateRegex.test(value) ? new Date(value) : value
}

export function convertDatesToIsoString<T>(value: T): DateToString<T> | string {
  if (Array.isArray(value)) {
    return value.map(item => convertDatesToIsoString(item)) as DateToString<T>
  } else if (typeof value === 'object' && value !== null) {
    const result: JsonObject = {}
    for (const [k, v] of Object.entries(value)) {
      const _value = v as JsonValue
      if (value.hasOwnProperty(k)) {
        result[k] = _value instanceof Date ? _value.toISOString() : convertDatesToIsoString(_value)
      }
    }
    return result as DateToString<T>
  }

  return value instanceof Date ? value.toISOString() : (value as DateToString<T>)
}

// エポックミリ秒を比較する
export const compareDates = (date1: number, date2: number): number => {
  if (date1 < date2) {
    return -1
  } else if (date1 > date2) {
    return 1
  } else {
    return 0
  }
}

export function jaFormatDateTime(date?: Date | string, formatStr = 'YYYY年MM月DD日 HH:mm') {
  if (!date) return ''

  if (typeof date === 'string') {
    date = new Date(date)
  }

  const year = date.getFullYear()
  const month = ('0' + (date.getMonth() + 1)).slice(-2)
  const day = ('0' + date.getDate()).slice(-2)
  const hours = ('0' + date.getHours()).slice(-2)
  const minutes = ('0' + date.getMinutes()).slice(-2)

  return formatStr
    .replace(/YYYY/g, year.toString())
    .replace(/MM/g, month)
    .replace(/DD/g, day)
    .replace(/HH/g, hours)
    .replace(/mm/g, minutes)
}

/**
 * 年・月・日を受け取り 'YYYY-MM-DD' 形式の日付文字列を返す
 * @param year 西暦年
 * @param month 月（1~12）
 * @param day 日（1~31）
 * @returns フォーマット済み日付文字列
 */
export function toDateString(year: number, month: number, day: number): string {
  const paddedMonth = String(month).padStart(2, '0')
  const paddedDay = String(day).padStart(2, '0')
  return [year, paddedMonth, paddedDay].join('-')
}

/**
 * 日付文字列と時間を結合し、ISO8601（秒単位）の文字列を返す
 * @param date 'YYYY-MM-DD'形式の日付文字列
 * @param hour 時間（0-23の数値）またはnull
 * @returns タイムスタンプの文字列またはnull（date または hour が無効な場合）
 */
export function combineDateTimeToTimestamp(
  date: string | null,
  hour: string | null
): string | null {
  if (!date || hour === null) return null
  const formattedHour = hour.padStart(2, '0')
  return `${date}T${formattedHour}:00:00+09:00`
}

/**
 * ISO8601（秒単位）またはnullを日付（文字列）と時間（数値またはnull）に分割する
 * @param timestamp ISO8601（秒単位）またはnull
 * @returns 日付（'YYYY-MM-DD'）と時間（時刻0-23またはnull）を含むオブジェクト
 */
export function splitDatetime(timestamp: string | null): {
  date: string | null
  hour: string | null
} {
  if (!timestamp) return { date: '', hour: '' }
  const dateObj = new Date(timestamp)
  const year = dateObj.getFullYear()
  const month = dateObj.getMonth() + 1
  const day = dateObj.getDate()
  const hour = dateObj.getHours()
  return {
    date: toDateString(year, month, day),
    hour: String(hour)
  }
}

/**
 * 与えられた日付からYYYYMMDDHHMMSSで返すフォーマッタ
 * @param d 日付型の日付
 * @returns YYYYMMDDhhmmss型式で日付を返す
 */
export const convertDateTimeToString = (d: Date): string => {
  const YYYY = d.getFullYear().toString()
  const MM = (d.getMonth() + 1).toString().padStart(2, '0')
  const DD = d.getDate().toString().padStart(2, '0')
  const hh = d.getHours().toString().padStart(2, '0')
  const mm = d.getMinutes().toString().padStart(2, '0')
  const ss = d.getSeconds().toString().padStart(2, '0')
  return YYYY + MM + DD + hh + mm + ss
}

/**
 * convertDateTimeToStringにて作成された文字列（YYYYMMDDHHMMSS)かどうか判定する
 * @param dateTime 生成されたDateTime文字列
 * @returns boolean
 */
export const isDateTimeString = (dateTime: string): boolean => {
  // 文字数判定がない場合、15文字以上の文字列が入ってきた場合にtrueとなってしまうため入れる
  if (dateTime.length !== 14) return false
  const pattern = /\d{4}(0\d|1[0-2])([0-2]\d|3[0,1])([0,1]\d|2[0-3])[0-5]\d[0-5]\d/
  return pattern.test(dateTime)
}
